import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback } from 'react';

import { useSaveCardInfoIntentToSetupMutation } from './__generated-gql-types__/useSaveCardInfo.generated';

/**
 * Saves payment method on the card element.
 * Make sure to use with <StripeCardElement /> and wrap the component that consumes this hook in <StripeProvider />
 * @returns saveCardInfo
 */
export const useSaveCardInfo = () => {
  const [intentToSetupAction] = useSaveCardInfoIntentToSetupMutation();
  const elements = useElements();
  const stripe = useStripe();

  const saveCardInfo = useCallback(
    async (
      setErrorMessage?: (errorMessage: string | null) => void,
    ): Promise<string | null> => {
      const cardElement = elements?.getElement(CardElement);
      const returnUrlData = { return_url: window.location.href };
      const paymentData =
        cardElement != null
          ? {
              payment_method: {
                card: cardElement,
              },
              ...returnUrlData,
            }
          : returnUrlData;

      if (cardElement != null && stripe) {
        const { data } = await intentToSetupAction();
        if (data?.intentToSetup) {
          const intentToSetupClientSecret = data.intentToSetup;
          const setupIntentResponse = await stripe.confirmCardSetup(
            intentToSetupClientSecret,
            paymentData,
          );
          if (setupIntentResponse.setupIntent?.payment_method) {
            return (
              'urn:stripePaymentMethod:' +
              (setupIntentResponse.setupIntent.payment_method as string)
            );
          } else {
            setErrorMessage?.(
              setupIntentResponse.error?.message ??
                'Invalid card to use. Please try again or use a different card.',
            );
            return null;
          }
        }
      }
      return null;
    },
    [intentToSetupAction, elements, stripe],
  );

  return saveCardInfo;
};
